import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import {
  Loader2,
  MapPin,
  MessageSquare,
  Phone,
  Mail,
  Globe,
  Building2,
  CheckCircle2,
  Clock,
  Wrench,
} from 'lucide-react';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from '@/lib/google-maps/libraries';
import { publicPost } from '@/lib/api/publicClient';
import { publicTrackingEndpoints } from '@/lib/api/endpoints';
import { formatDateTimeInTimeZone, formatTimeZoneLabel } from '@/lib/date-values';
import {
  usePublicTrackingPage,
  usePublicTrackingData,
  type TrackingBusiness,
} from '@/hooks/public/usePublicTracking';
import { formatElapsedMinutes } from '@/lib/time/elapsed';
import { arrivalLine, arrivalState } from '@/lib/tracking/arrival';
import { businessContactLinks } from '@/lib/tracking/contact';
import { movingParts } from '@/lib/tracking/live-source';
import { usePublicPageScroll } from '@/components/public/usePublicPageScroll';

export const Route = createFileRoute('/(public)/t/$token')({
  component: TrackingPage,
});

const MAP_STYLE = { width: '100%', height: '100%' } as const;
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const ROUTE_COLOR = '#f97316'; // orange-500 — the route line
const INK = '#111827'; // near-black — markers + accents

/** Statuses past which there's nothing live to show. */
const TERMINAL = new Set(['COMPLETED', 'CANCELLED', 'CANCELLATION_PENDING', 'EXPIRED', 'SKIPPED']);

/**
 * Company websites are stored as a free-form string, so a value like `acme.com`
 * would resolve as a relative path and other schemes could sneak onto this
 * public page. Coerce bare domains to `https://` and allow only http(s) —
 * anything else yields `null` and the link is dropped. Mirrors the public
 * invoice page's `toExternalUrl`.
 */
function toExternalUrl(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

type LatLng = { lat: number; lng: number };

/** Live route between the tech and the destination, and what it implies. */
interface RouteInfo {
  directions: google.maps.DirectionsResult;
  minutes: number;
  meters: number;
  distanceText: string;
}

function initials(name: string | null): string {
  if (!name) return '🙂';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** A round black "car" badge for the technician's live position. */
function carIcon(): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
    <circle cx="23" cy="23" r="18" fill="${INK}" stroke="#fff" stroke-width="3"/>
    <path d="M15 26.5v-3.2l1.6-4.1a2 2 0 0 1 1.9-1.2h8.9a2 2 0 0 1 1.9 1.2l1.6 4.1v3.2a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-.8H18v.8a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1Z" fill="#fff"/>
    <circle cx="18.5" cy="23.8" r="1.3" fill="${INK}"/>
    <circle cx="27.5" cy="23.8" r="1.3" fill="${INK}"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(46, 46),
    anchor: new google.maps.Point(23, 23),
  };
}

/** A destination pin for the customer's address. */
function homeIcon(): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48">
    <path d="M20 47c9-9.5 15-16.4 15-24.2A15 15 0 0 0 5 22.8C5 30.6 11 37.5 20 47Z" fill="${INK}" stroke="#fff" stroke-width="2.5"/>
    <path d="M20 15l7 6v9h-4.5v-5.5h-5V30H13v-9l7-6Z" fill="#fff"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(40, 48),
    anchor: new google.maps.Point(20, 46),
  };
}

/**
 * Page frame: a pinned brand bar that stays visible as the customer scrolls,
 * over a light, centered, mobile-first column. Keeping the company always in
 * view (and the page a normal, single-scroll column) is what makes it feel like
 * a proper product rather than a long form.
 */
function Shell({
  business,
  children,
  /** Only the live two-pane tracking view widens on tablet; the centered
   *  empty/loading states stay a single mobile-width column so they don't look
   *  stranded in a stretched shell. */
  wide = false,
}: {
  business?: TrackingBusiness | null;
  children: ReactNode;
  wide?: boolean;
}) {
  usePublicPageScroll();
  const name = business?.name?.trim() || 'Live tracking';
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* One centered app column — header AND content share this width so nothing
          is wider than the rest. No overflow-hidden here (it would break the
          sticky header). */}
      <div
        className={`mx-auto flex min-h-screen w-full max-w-md flex-col bg-white shadow-sm ${
          wide ? 'md:max-w-5xl' : ''
        }`}
      >
        <header className="sticky top-0 z-30 flex items-center gap-2.5 border-b border-gray-100 bg-white/95 px-4 py-2.5 backdrop-blur">
          {business?.logo ? (
            <img
              src={business.logo}
              alt={name}
              className="h-9 w-9 shrink-0 rounded-lg border border-gray-200 bg-white object-contain p-0.5"
              loading="lazy"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-sm font-semibold text-white">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight text-gray-900">{name}</p>
            <p className="text-[11px] leading-tight text-gray-400">Live service tracking</p>
          </div>
        </header>
        <main className="flex-1 pb-10">{children}</main>
      </div>
    </div>
  );
}

function CompanyFooter({
  business,
  /** In the narrow tablet side panel there isn't room for the 2-col contact
   *  grid — keep it single-column until an xl viewport so phone/website/address
   *  don't get squeezed and wrap. */
  compact = false,
}: {
  business: TrackingBusiness | null;
  compact?: boolean;
}) {
  const websiteUrl = toExternalUrl(business?.website);
  const hasContact =
    business && (business.phone || business.email || websiteUrl || business.address);
  return (
    <footer className="mt-4 space-y-3">
      {business?.name && hasContact && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-400" />
            <p className="text-sm font-semibold text-gray-900">{business.name}</p>
          </div>
          <div
            className={`mt-3 grid gap-2.5 text-sm text-gray-600 ${
              compact ? 'xl:grid-cols-2' : 'sm:grid-cols-2'
            }`}
          >
            {business.phone && (
              <a
                href={businessContactLinks(business.phone)?.tel ?? `tel:${business.phone}`}
                className="flex items-center gap-2 transition-colors hover:text-gray-900"
              >
                <Phone className="h-4 w-4 text-gray-400" />
                {business.phone}
              </a>
            )}
            {business.email && (
              <a
                href={`mailto:${business.email}`}
                className="flex items-center gap-2 transition-colors hover:text-gray-900"
              >
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="truncate">{business.email}</span>
              </a>
            )}
            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 transition-colors hover:text-gray-900"
              >
                <Globe className="h-4 w-4 text-gray-400" />
                <span className="truncate">
                  {websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </span>
              </a>
            )}
            {business.address && (
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <span>{business.address}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <p className="pb-3 text-center text-xs text-gray-400">
        Powered by <span className="font-medium text-gray-500">Notifi</span> · updates live
      </p>
    </footer>
  );
}

function TrackingPage() {
  const { token } = Route.useParams();
  const { data: page, isLoading, isError } = usePublicTrackingPage(token);

  const isLive = Boolean(page) && !TERMINAL.has(page!.status);
  const { data: live } = usePublicTrackingData(token, isLive);

  // Live poll wins over the initial snapshot for the moving parts — including
  // when what it reports is nothing (see movingParts).
  const { status, eta: backendEta, techLocation } = movingParts(live, page);
  const destination = page?.destination ?? null;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [route, setRoute] = useState<RouteInfo | null>(null);

  const techPoint = useMemo<LatLng | null>(
    () => (techLocation ? { lat: techLocation.lat, lng: techLocation.lng } : null),
    [techLocation],
  );

  // Compute the real road route (and its live drive time) whenever the tech
  // moves or the destination resolves.
  useEffect(() => {
    if (!isLoaded || !techPoint || !destination) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    const svc = new google.maps.DirectionsService();
    svc
      .route({
        origin: techPoint,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      })
      .then((res) => {
        if (cancelled) return;
        const leg = res.routes[0]?.legs[0];
        setRoute({
          directions: res,
          minutes: leg?.duration ? Math.round(leg.duration.value / 60) : 0,
          meters: leg?.distance?.value ?? 0,
          distanceText: leg?.distance?.text ?? '',
        });
      })
      .catch(() => {
        if (!cancelled) setRoute(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, techPoint, destination]);

  // Keep both the tech and the destination in frame.
  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const pts = [techPoint, destination].filter(Boolean) as LatLng[];
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setCenter(pts[0]);
      map.setZoom(14);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 64);
  }, [techPoint, destination]);

  useEffect(() => {
    fitBounds();
  }, [fitBounds]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fitBounds();
    },
    [fitBounds],
  );

  if (isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Shell>
    );
  }

  if (isError || !page) {
    return (
      <Centered title="Tracking unavailable" body="This tracking link is invalid or has expired." />
    );
  }

  if (TERMINAL.has(status)) {
    return (
      <Centered
        business={page.business}
        icon={<CheckCircle2 className="h-10 w-10 text-gray-900" />}
        title="Visit complete"
        body="This appointment is no longer in progress. Thanks for choosing us!"
      />
    );
  }

  // ── Derive the headline / phase from what we actually know ──────────────────
  // A countdown may only come from a tech who is actually moving: the road route
  // we just computed from their live position, or a live-sourced backend ETA.
  // A `static` ETA is the drive time the visit was PLANNED with — showing it in
  // the countdown told a customer "22 min away" about a technician who hadn't
  // left the shop, and kept saying it after they'd finished for the day.
  const driveMinutes =
    route?.minutes ?? (backendEta?.source === 'live' ? backendEta.driveMinutes : null);
  const atSite = route != null && route.meters < 150;
  const arriving = !atSite && driveMinutes != null && driveMinutes <= 2;

  let phaseIndex: number;
  let headline: string;
  if (!techPoint) {
    phaseIndex = 0;
    headline = page.technicianName
      ? `${firstName(page.technicianName)} will head out soon`
      : 'Your technician will head out soon';
  } else if (atSite) {
    phaseIndex = 3;
    headline = page.technicianName
      ? `${firstName(page.technicianName)} has arrived`
      : 'Your technician has arrived';
  } else if (arriving) {
    phaseIndex = 2;
    headline = 'Arriving now';
  } else {
    phaseIndex = 1;
    headline = page.technicianName
      ? `${firstName(page.technicianName)} is on the way`
      : 'Your technician is on the way';
  }

  const arrivalClock = route
    ? new Date(Date.now() + route.minutes * 60_000).toISOString()
    : (backendEta?.arrivalTime ?? null);

  const headlineArrival = arrivalLine(
    arrivalState({
      atSite,
      driveMinutes,
      arrivalAt: arrivalClock,
      hasTechLocation: techPoint != null,
      now: Date.now(),
    }),
    (iso) =>
      `${formatDateTimeInTimeZone(iso, page.timezone, { hour: 'numeric', minute: '2-digit' })} · ${formatTimeZoneLabel(page.timezone)}`,
  );

  return (
    <Shell business={page.business} wide>
      {/* Phone: one scrolling column (map over details). Tablet+: a two-pane
          row — a tall hero map beside a scrollable details panel. */}
      <div className="md:flex md:items-start">
        {/* ── Hero map ─────────────────────────────────────────────────────── */}
        <div className="relative h-[360px] w-full bg-gray-100 sm:h-[420px] md:sticky md:top-14 md:h-[calc(100vh-3.5rem)] md:w-3/5 md:self-start">
          {isLoaded && apiKey ? (
            <GoogleMap
              mapContainerStyle={MAP_STYLE}
              center={techPoint ?? destination ?? DEFAULT_CENTER}
              zoom={13}
              onLoad={onMapLoad}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'greedy',
                clickableIcons: false,
                styles: MAP_MUTED,
              }}
            >
              {route ? (
                <DirectionsRenderer
                  directions={route.directions}
                  options={{
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                      strokeColor: ROUTE_COLOR,
                      strokeWeight: 5,
                      strokeOpacity: 0.95,
                    },
                  }}
                />
              ) : null}
              {destination ? (
                <Marker position={destination} icon={homeIcon()} title="Your address" />
              ) : null}
              {techPoint ? (
                <Marker
                  position={techPoint}
                  icon={carIcon()}
                  title={page.technicianName ?? 'Technician'}
                  zIndex={999}
                />
              ) : null}
            </GoogleMap>
          ) : (
            <div className="flex h-full items-center justify-center text-gray-300">
              <MapPin className="h-8 w-8" />
            </div>
          )}

          {/* Distance chip */}
          {route && !atSite ? (
            <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow">
              {route.distanceText} away
            </div>
          ) : null}
        </div>

        {/* ── Details: overlaps the map on phone; a bordered right column that
            scrolls beside the sticky map on tablet. ── */}
        <div className="relative -mt-6 space-y-4 rounded-t-3xl bg-white p-5 shadow-[0_-6px_28px_rgba(0,0,0,0.08)] md:mt-0 md:min-h-[calc(100vh-3.5rem)] md:w-2/5 md:space-y-5 md:rounded-none md:border-l md:border-gray-100 md:p-6 md:shadow-none">
          {/* Technician */}
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-900 text-lg font-semibold text-white">
              {initials(page.technicianName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-gray-900">
                {page.technicianName ?? 'Your technician'}
              </p>
              <p className="text-xs text-gray-500">Your technician</p>
            </div>
            <ContactActions
              phone={page.business?.phone ?? null}
              token={token}
              technicianName={page.technicianName}
            />
          </div>

          <div className="h-px bg-gray-100" />

          {/* Status + ETA */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900">{headline}</h1>
              <p className="mt-0.5 text-sm text-gray-500">{headlineArrival}</p>
            </div>
            {!atSite && driveMinutes != null ? (
              <div className="shrink-0 rounded-2xl bg-gray-900 px-3.5 py-2 text-center text-white">
                {/* Past an hour the raw minute count stops being readable at a
                    glance — "94" over "min" is a number to decode, "1h 34m" is a
                    drive time. */}
                <div className="text-2xl font-bold leading-none">
                  {formatElapsedMinutes(driveMinutes)}
                </div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-white/70">
                  away
                </div>
              </div>
            ) : null}
          </div>

          <Stepper activeIndex={phaseIndex} />

          {/* Journey: technician → your address */}
          <div className="rounded-2xl border border-gray-100 p-3.5">
            <div className="flex gap-3">
              <div className="flex flex-col items-center pt-1">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-900" />
                <span className="my-1 w-px flex-1 border-l border-dashed border-gray-300" />
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: ROUTE_COLOR }}
                />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">Technician</p>
                  <p className="text-sm font-medium text-gray-900">
                    {techPoint ? 'On the way to you' : 'Preparing to depart'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">Arriving at</p>
                  <p className="text-sm font-medium text-gray-900">
                    {page.destinationLabel ?? 'Your location'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2.5 rounded-2xl border border-gray-100 p-3 text-sm">
            <DetailRow
              icon={<Wrench className="h-4 w-4 text-gray-400" />}
              label="Service"
              value={page.serviceType ?? 'Service visit'}
            />
            <DetailRow
              icon={<Clock className="h-4 w-4 text-gray-400" />}
              label="Scheduled"
              value={scheduledText(page)}
            />
          </div>

          {/* Company info sits INSIDE the right column on tablet, so it reads as
              part of that panel instead of an orphaned card under the two panes. */}
          <div className="hidden md:block">
            <CompanyFooter business={page.business} compact />
          </div>
        </div>
      </div>

      {/* Phone: company info as its own card below the details sheet. */}
      <div className="px-4 md:hidden">
        <CompanyFooter business={page.business} />
      </div>
    </Shell>
  );
}

function scheduledText(page: {
  timezone: string;
  startAt: string | null;
  arrivalWindow: { start: string; end: string } | null;
}): string {
  if (page.arrivalWindow) {
    const start = formatDateTimeInTimeZone(page.arrivalWindow.start, page.timezone, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const end = formatDateTimeInTimeZone(page.arrivalWindow.end, page.timezone, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${start} – ${end}`;
  }
  if (page.startAt) {
    return formatDateTimeInTimeZone(page.startAt, page.timezone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return 'To be confirmed';
}

function DetailRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <span className="w-20 shrink-0 text-gray-400">{label}</span>
      <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{value}</span>
    </div>
  );
}

const STEPS = ['Scheduled', 'On the way', 'Arriving', 'On site'] as const;

function Stepper({ activeIndex }: { activeIndex: number }) {
  return (
    <div className="mt-4 flex items-center">
      {STEPS.map((label, i) => {
        const done = i <= activeIndex;
        return (
          <div key={label} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`h-2.5 w-2.5 rounded-full ${done ? 'bg-gray-900' : 'bg-gray-200'}`} />
              <span
                className={`mt-1 text-[10px] font-medium ${done ? 'text-gray-900' : 'text-gray-400'}`}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 ? (
              <div
                className={`mx-1 -mt-4 h-0.5 flex-1 ${i < activeIndex ? 'bg-gray-900' : 'bg-gray-200'}`}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Centered({
  icon,
  title,
  body,
  business,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  business?: TrackingBusiness | null;
}) {
  return (
    <Shell business={business ?? null}>
      <div className="px-4">
        <div className="py-20 text-center">
          {icon ? <div className="mb-3 flex justify-center">{icon}</div> : null}
          <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
          <p className="mt-2 text-sm text-gray-500">{body}</p>
        </div>
        {business ? <CompanyFooter business={business} /> : null}
      </div>
    </Shell>
  );
}

/**
 * Call / Message the business, handed off to the phone's own apps.
 *
 * The customer is on their phone with a live job in front of them, so the fast
 * path is the dialer and the messaging app they already use — not a compose box
 * on a web page whose reply lands somewhere they have to come back to. Both
 * point at the tenant's main line: it is the number already printed in the
 * footer, and it reaches whoever is actually covering the phones.
 *
 * A tenant with no dialable number on file gets the token-scoped relay instead.
 * Deep links are only ever as good as the number behind them, and rendering
 * nothing would leave that customer on a live job with no way to reach anybody
 * — worse than what this replaced. The relay needs no number to work, so it is
 * the floor under this: the native handoff when we can, the relay when we
 * can't, never neither.
 */
function ContactActions({
  phone,
  token,
  technicianName,
}: {
  phone: string | null;
  token: string;
  technicianName: string | null;
}) {
  const links = businessContactLinks(phone);
  if (!links) {
    return <MessageTechButton token={token} technicianName={technicianName} />;
  }
  return (
    <div className="flex shrink-0 items-center gap-2">
      <a
        href={links.tel}
        aria-label="Call us"
        className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50"
      >
        <Phone className="h-4 w-4" />
      </a>
      <a
        href={links.sms}
        aria-label="Message us"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-white shadow-sm transition hover:bg-gray-800"
      >
        <MessageSquare className="h-4 w-4" />
      </a>
    </div>
  );
}

/**
 * §9.4 — "Message tech": the customer's half of the masked relay. Posts to the
 * token-scoped public endpoint; the reply comes back over SMS. No number shown.
 *
 * Now the fallback rather than the default (see ContactActions), and the reason
 * it is still here: it is the one contact path that works with nothing on file
 * but the token in the URL, so it is what a tenant with no usable phone number
 * falls back to. It also keeps CUSTOMER_ESCALATION reachable from this view.
 */
function MessageTechButton({
  token,
  technicianName,
}: {
  token: string;
  technicianName: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const who = technicianName?.trim().split(/\s+/)[0] || 'your technician';

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setStatus('sending');
    try {
      await publicPost(publicTrackingEndpoints.message(token), { text: body });
      setText('');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Message ${who}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white shadow-sm transition hover:bg-gray-800"
      >
        <MessageSquare className="h-4 w-4" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={() => status !== 'sending' && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-xl sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-700" />
              <h2 className="font-semibold text-gray-900">Message {who}</h2>
            </div>
            {status === 'sent' ? (
              <p className="py-4 text-sm text-gray-700">
                Message sent — {who} will text you back here.
              </p>
            ) : (
              <>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  autoFocus
                  placeholder={`Send a message to ${who}…`}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={send}
                    disabled={status === 'sending' || !text.trim()}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {status === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Send
                  </button>
                </div>
                {status === 'error' ? (
                  <p className="mt-2 text-xs text-red-600">Couldn’t send — try again.</p>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Subtle, low-contrast map so the orange route + markers are the focus. */
const MAP_MUTED: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];
